import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { createCheckout, formatPrice } from "../lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const CartPage = () => {
  const { items, totals, updateQuantity, removeItem, itemKey } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  const onCheckout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          type: i.type,
          slug: i.slug,
          colourway: i.colourway || null,
          size: i.size || null,
          grip: i.grip || null,
          quantity: i.quantity,
        })),
        origin_url: window.location.origin,
      };
      const { url } = await createCheckout(payload);
      window.location.href = url;
    } catch {
      toast.error("Could not start checkout. Try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="container-x py-32 text-center" data-testid="cart-page-empty">
        <p className="overline mb-3">Empty Cart</p>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tightest font-bold leading-[0.95]">
          Build your rotation.
        </h1>
        <p className="text-mute mt-4">Pick a colourway. Pack the matchday rotation.</p>
        <button
          type="button"
          onClick={() => navigate("/shop")}
          className="btn-primary mt-6"
          data-testid="cart-page-shop"
        >
          Shop grip socks
        </button>
      </div>
    );
  }

  return (
    <div className="container-x py-16" data-testid="cart-page">
      <p className="overline mb-3">Your Cart</p>
      <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tightest font-bold leading-[0.95] mb-10">
        {totals.count} item{totals.count === 1 ? "" : "s"} in the rotation.
      </h1>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 border border-line bg-white">
          <ul className="divide-y divide-line">
            {items.map((item) => {
              const key = itemKey(item);
              return (
                <li key={key} className="p-5 flex gap-4" data-testid={`cart-page-item-${item.slug}`}>
                  <div className="w-24 h-24 border border-line" style={{ backgroundColor: item.swatch || "#F4F6F8" }} />
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-xs text-mute mt-1">
                          {item.colourwayLabel ? `${item.colourwayLabel} · ` : ""}
                          {item.size || ""}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(key)}
                        className="text-mute hover:text-ink p-1"
                        data-testid={`cart-page-remove-${item.slug}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex border border-line">
                        <button
                          type="button"
                          onClick={() => updateQuantity(key, item.quantity - 1)}
                          className="w-8 h-8 text-sm hover:bg-line"
                        >
                          −
                        </button>
                        <span className="w-10 h-8 inline-flex items-center justify-center mono text-sm">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(key, item.quantity + 1)}
                          className="w-8 h-8 text-sm hover:bg-line"
                        >
                          +
                        </button>
                      </div>
                      <span className="mono">{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        <aside className="lg:col-span-4 bg-white border border-line p-6 h-fit">
          {!totals.qualifiesFree && totals.remainingForFree > 0 && (
            <div className="mb-5 pb-5 border-b border-line" data-testid="cart-page-shipping-progress">
              <p className="text-[11px] tracking-[0.16em] uppercase font-bold mb-2">
                Add {formatPrice(totals.remainingForFree)} for free shipping
              </p>
              <div className="h-1 bg-line overflow-hidden">
                <div
                  className="h-full bg-ink transition-all duration-500"
                  style={{
                    width: `${Math.min(100, (totals.subtotal / (totals.freeThreshold || 50)) * 100)}%`,
                  }}
                />
              </div>
            </div>
          )}
          {totals.qualifiesFree && (
            <p
              className="mb-5 pb-5 border-b border-line text-[11px] tracking-[0.16em] uppercase font-bold"
              data-testid="cart-page-free-unlocked"
            >
              Free UK shipping unlocked ·
            </p>
          )}
          <div className="flex items-center justify-between mb-2">
            <span className="overline">Subtotal</span>
            <span className="mono" data-testid="cart-page-subtotal">{formatPrice(totals.subtotal)}</span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="overline">Shipping</span>
            <span className="mono" data-testid="cart-page-shipping">
              {totals.shipping === 0 ? "Free" : formatPrice(totals.shipping)}
            </span>
          </div>
          <div className="h-px bg-line my-4" />
          <div className="flex items-center justify-between mb-6">
            <span className="overline">Total</span>
            <span className="font-display text-2xl" data-testid="cart-page-total">{formatPrice(totals.total)}</span>
          </div>
          <button
            type="button"
            onClick={onCheckout}
            disabled={loading}
            className="btn-primary w-full"
            data-testid="cart-page-checkout"
          >
            {loading ? "Redirecting…" : "Secure checkout"}
          </button>
          <p className="mt-3 text-[11px] text-mute text-center mono">
            Apple Pay & Google Pay supported
          </p>
          <Link to="/shop" className="block text-center mt-3 text-[11px] tracking-[0.18em] uppercase text-mute hover:text-ink">
            Continue shopping
          </Link>
        </aside>
      </div>
    </div>
  );
};

export default CartPage;
